import React, { Fragment, useEffect, useState } from "react";
import { Box, Button, Card, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid";
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { formatDate } from "../../../helpers/dateHelper";
import { DECLINE, EDIT, EMAIL_VERIFIED, PENDING, STATUS_COLORS } from "../../../enums/statusEnums";
import { approveUser, getUserList } from "../../../config/apisauce";
import { DECLINE_CHECKLIST, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { ReasonDialog } from "../dialogs/ReasonDialog";
import useNotiStack from "../../../hooks/useNotiStack";
import LoadingButton from "@mui/lab/LoadingButton";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { UsersThree, Warning } from "@phosphor-icons/react";
import { ProfileDetails } from "./profileDetails/ProfileDetails";
import { router } from "@inertiajs/react";
import useStore from "../../../states/store";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import { GenericCircleIcon } from "../../../components/generic/GenericCircleIcon";
import { snakeToSentence } from "../../../helpers/stringHelper";

export const IncomingProfileApproval = ({ reloadStats, isHomePage = true, initialParams = { status: PENDING }, isBypassOndato = false }) => {
  const { success } = useNotiStack();
  const [, { openLoader, closeLoader }] = useStore();
  const [incomingPofileApproval, setIncomingProfileApproval] = useState({});
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [openProfileDetailsDialog, setOpenProfileDetailsDialog] = useState(false);
  const [dialogDetails, setDialogDetails] = useState({});
  const [details, setDetails] = useState('');
  const [stillProcessing, setStillProcessing] = useState([]);
  const [userId, setUserId] = useState(null);
  const [pagination, setPagination] = useState({ pageSize: isHomePage ? 5 : 10, page: 0 });
  const [acceptConfirmationState, setAcceptConfirmationState] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const genericColumnProps = {
    editable: false,
    sortable: false,
    resizable: false
  };

  const handleOpenProfileDetailsDialog = (id) => {
    setUserId(id);
    setOpenProfileDetailsDialog(true);
  };

  const handleCloseProfileDetailsDialog = () => {
    setUserId(null);
    setOpenProfileDetailsDialog(false);
  };

  const handleOpenDialog = (label, data, checklists, action) => {
    setDialogDetails({ label: label, data: data, checklists: checklists, action: action });
    setOpenReasonDialog(true);
    setOpenProfileDetailsDialog(false);
  };

  const handleCloseReasonDialog = () => {
    setOpenReasonDialog(false);
    setDialogDetails({});
  };

  const handleRemoveFromList = (id) => {
    setIncomingProfileApproval((prev) => {
      const currentList = { ...prev };
      currentList.data = currentList.data?.filter((item) => item?.id !== id);
      return currentList;
    });
    isHomePage && reloadStats();
  };

  const handleAcceptUser = async (values) => {
    setStillProcessing((prev) => ([...prev, values?.id]));
    const params = {
      user_id: values?.id,
      email: values?.email,
      name: values?.name,
    };
    const { ok, data } = await approveUser(params);
    if (ok) {
      handleRemoveFromList(data?.id);
      success('User has been accepted!');
    }
    setAcceptConfirmationState(false);
    setStillProcessing((prev) => prev?.filter(item => item !== values?.id));
  };

  const handleAcceptChecker = (values) => {
    if (isBypassOndato) {
      setAcceptConfirmationState(true);
      setSelectedCreator(values);
    } else {
      handleAcceptUser(values);
    }
  };

  const fetchProfileApproval = async () => {
    openLoader();
    const params = {
      ...INITIAL_GET_PARAMS,
      ...initialParams,
      per_page: pagination?.pageSize,
      page: pagination?.page + 1
    };
    const response = await getUserList(params);
    if (response.ok) {
      setIncomingProfileApproval(response?.data);
    }
    closeLoader();
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      renderCell: ({ row }) => {
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1.5} mt={0.5}>
            <GenericAvatar location={row?.details?.profile_picture?.location} avatarId={row?.id} size={'42px'} backgroundColor={'background.default'} />
            <Typography fontWeight={700}>{row?.name}</Typography>
          </Stack>
        );
      },
      ...genericColumnProps
    },
    {
      field: 'profession',
      headerName: 'Profession',
      flex: 1,
      renderCell: ({ row }) => row?.details?.profession,
      ...genericColumnProps
    },
    {
      field: 'created_at',
      headerName: 'Request Date',
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...genericColumnProps
    },
    {
      field: 'referral_code',
      headerName: 'Referral Code',
      flex: 1,
      renderCell: ({ row }) => row?.details?.meta?.reffered_by ?? <Typography mt={1.7} color="grey.light">N/A</Typography>,
      ...genericColumnProps
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: isBypassOndato ? 1 : 0.7,
      renderCell: ({ row }) => {
        return (
          <Typography
            mt={1.7}
            fontWeight={700}
            textTransform={'capitalize'}
            color={isBypassOndato ? 'error' : STATUS_COLORS[row?.status]}
          >
            {row?.status === EMAIL_VERIFIED ? 'Ondato Unverified' : snakeToSentence(row?.status)}
          </Typography>
        );
      },
      ...genericColumnProps
    },
    ...(!isBypassOndato ? [
      {
        field: 'request-edit',
        headerName: 'Request Edit',
        flex: 1.4,
        renderCell: ({ row }) => {
          const processing = stillProcessing?.includes(row?.id);
          return (
            <Button fullWidth disabled={processing} variant="outlined" onClick={() => handleOpenDialog('Edit', row, [], EDIT)}>Request Edit</Button>
          );
        },
        ...genericColumnProps
      },
    ] : []),
    {
      field: 'details',
      headerName: 'Details',
      flex: isBypassOndato ? 1 : 2.5,
      renderCell: ({ row }) => {
        return (
          <Stack gap={1} direction={'row'} mt={1.1}>
            <Button fullWidth variant="contained" sx={{ color: '#FFF' }} onClick={() => handleOpenProfileDetailsDialog(row?.id)}>Details</Button>
            {
              !isBypassOndato && <Button
                disabled={!row?.ondato_url}
                fullWidth
                variant="contained"
                onClick={() => window.open(row?.ondato_url, '_blank')}
              >
                Ondato Details
              </Button>
            }
          </Stack>
        );
      },
      ...genericColumnProps
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: isBypassOndato ? 1.6 : 2.5,
      renderCell: ({ row }) => {
        const processing = stillProcessing?.includes(row?.id);
        return (
          <Stack gap={1} direction={'row'} mt={1.1}>
            <Button disabled={processing} onClick={() => handleOpenDialog('Declining', row, DECLINE_CHECKLIST, DECLINE)} fullWidth variant="contained" color="error" sx={{ color: '#FFF' }}>Decline</Button>
            <LoadingButton
              fullWidth
              onClick={() => handleAcceptChecker(row)}
              size="small"
              loading={processing}
              variant="contained"
              disabled={processing}
              color={isBypassOndato ? 'warning' : "success"}
            >
              Accept
            </LoadingButton>
          </Stack>
        );
      },
      ...genericColumnProps
    }
  ];

  useEffect(() => {
    fetchProfileApproval();
  }, [pagination]);

  return (
    <Fragment>
      <Card sx={{ boxShadow: 'none' }}>
        <Grid container spacing={1.5}>
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Typography variant="h6" fontWeight={700} color="primary">
              {
                isBypassOndato ? 'Ondato Unverified & Rejected Accounts' : 'Incoming Profile Approval'
              }
            </Typography>
          </Grid>
          {
            isHomePage && <Grid container item justifyContent={'flex-end'} size={{ xs: 12, sm: 6 }}>
              <Button variant="outlined" sx={{ width: { xs: '100%', sm: '102px' } }} onClick={() => router.visit('/admin-collab-creators')}>See All</Button>
            </Grid>
          }
        </Grid>

        <Typography fontWeight={500} mb={2}>
          {
            isBypassOndato ? 'These accounts have completed all required steps but remain unverified or have been rejected by Ondato.' : 'You have new requests for profile approval. Review the details and decide who you’d like to work with.'
          }
        </Typography>
        {
          incomingPofileApproval?.data?.length > 0 ? <GenericMuiDatagrid
            {...incomingPofileApproval}
            hideFooter={isHomePage}
            columns={columns}
            height={isHomePage ? 'auto' : '64vh'}
            setPagination={setPagination}
          >
            {
              incomingPofileApproval?.data?.map((user, index) => {
                return (
                  <Fragment key={`user-key-${index}`} >
                    <Box sx={{ padding: '16px' }}>
                      <Grid container>
                        <Grid container item size={10} alignItems={'center'}>
                          <Grid item size={5}>
                            <GenericAvatar location={user?.details?.profile_picture?.location} avatarId={user?.id} size={'92px'} backgroundColor={'background.default'} />
                          </Grid>
                          <Grid item size={7}>
                            <Stack>
                              <Typography fontWeight={700} noWrap>{user?.name}</Typography>
                              <Typography noWrap>{user?.details?.profession}</Typography>
                              <Typography
                                fontWeight={700}
                                textTransform={'capitalize'}
                                color={isBypassOndato ? 'error' : STATUS_COLORS[user?.status]}
                              >
                                {user?.status === EMAIL_VERIFIED ? 'Ondato Unverified' : snakeToSentence(user?.status)}
                              </Typography>
                            </Stack>
                          </Grid>
                        </Grid>
                        <Grid container item justifyContent={'flex-end'} size={2}>
                          <Typography color="text.light">{formatDate(new Date(user?.created_at), DAY_ONLY_FORMAT)}</Typography>
                        </Grid>
                      </Grid>
                      <Stack direction={'row'} gap={0.5} mt={1.5}>
                        <Button fullWidth variant="contained" color="error" onClick={() => handleOpenDialog('Declining', user, DECLINE_CHECKLIST, DECLINE)}>Decline</Button>
                        <Button fullWidth variant="contained" color={isBypassOndato ? 'warning' : "success"} onClick={() => handleAcceptChecker(user)}>Accept</Button>
                      </Stack>
                      <Stack direction={'column'} mt={1.5} gap={1.5}>
                        <Button fullWidth variant="contained" onClick={() => handleOpenProfileDetailsDialog(user?.id)}>Details</Button>
                        {
                          !isBypassOndato && <Fragment>
                            <Button disabled={!user?.ondato_url} fullWidth variant="contained" onClick={() => window.open(user?.ondato_url, '_blank')}>
                              Ondato Details
                            </Button>
                            <Button fullWidth variant="outlined" onClick={() => handleOpenDialog('Edit', user, [], EDIT)}>Request Edit</Button>
                          </Fragment>
                        }
                      </Stack>
                    </Box>
                    <Divider sx={{ borderColor: 'borders.main' }} />
                  </Fragment>
                );
              })
            }
          </GenericMuiDatagrid> : <GenericNoData icon={<UsersThree size={40} />} height={'67vh'} label={'No incoming approval'} />
        }
      </Card>
      <ReasonDialog
        details={details}
        setDetails={setDetails}
        dialogDetails={dialogDetails}
        openDialog={openReasonDialog}
        handleRemoveFromList={handleRemoveFromList}
        handleCloseDialog={() => handleCloseReasonDialog()}
      />
      <ProfileDetails
        isBypassOndato={isBypassOndato}
        openDialog={openProfileDetailsDialog}
        userId={userId}
        handleClose={handleCloseProfileDetailsDialog}
        handleOpenReasonDialog={handleOpenDialog}
        handleAcceptUser={handleAcceptChecker}
        refetchCreatorList={() => fetchProfileApproval()}
      />
      <GenericDialog
        title={'Bypass Verification'}
        width={'550px'}
        openDialog={acceptConfirmationState}
        handleClose={() => setAcceptConfirmationState(false)}
      >
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'} mb={2}>
          <GenericCircleIcon color={'primary.main'}>
            <Warning size={96} weight="thin" />
          </GenericCircleIcon>
        </Box>
        <Stack gap={3} alignItems={'center'}>
          <Typography variant="subtitle1" textAlign={'center'}>
            You are about to accept a user <b>{selectedCreator?.status === EMAIL_VERIFIED ? 'without Ondato verification.' : 'Ondato Rejected.'}</b>
            <br></br>
            Are you sure you want to continue?
          </Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} mt={2}>
          <Button variant="outlined" sx={{ width: { xs: '100%', sm: '250px' } }} onClick={() => setAcceptConfirmationState(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            sx={{ width: { xs: '100%', sm: '250px' } }}
            onClick={() => { handleAcceptUser(selectedCreator); setSelectedCreator(null); setAcceptConfirmationState(false); }}
          >
            Approve Anyway
          </Button>
        </Stack>
      </GenericDialog>
    </Fragment >
  );
};